import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Decimal from "decimal.js";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import ListGroup from "react-bootstrap/ListGroup";
import Button from "react-bootstrap/Button";
import TextInput from "../components/TextInput";
import {
  PaymentIcon,
  DialpadIcon,
  DeleteForeverIcon,
} from "../components/icons";

import { MoneyInput } from "../components/NumberInput";
import toPriceDisplay from "../utils/toPriceDisplay";
import {
  setFormAnswer,
  setPaymentOnline,
  setPaymentOffline,
  setPayment,
  setCash,
  selectCard,
  removeCard,
  clearPayment,
  selectStatefulCosts,
  PIX_OPTION,
  MERCADO_PAGO_OPTION,
} from "../ducks/checkoutSlice";
import PaymentCardImage from "../components/PaymentCardImage";
import pixURL from "../assets/payment-flat-rounded/pix.png";
import mpURL from "../assets/payment-flat-rounded/mercado-pago.svg";
import CardMercadoPago from "./CardMercadoPago";
import { CheckoutRow } from "../components/CheckoutSection";
import { trackPaymentInfo } from "../ducks/tracking";

const styles = {
  body: {
    margin: 10,
  },
  input: {
    marginTop: 30,
  },
  cover: {
    backgroundColor: "rgb(57, 158, 227)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    marginBottom: 16,
  },
  image: {
    padding: 10,
  },
};

export default function PaymentSelection() {
  const dispatch = useDispatch();
  const [cardToDelete, setCardToDelete] = React.useState(null);
  const costs = useSelector(selectStatefulCosts);
  const {
    toGo,
    paymentOnline,
    paymentOffline,
    payment,
    cardSelected,
    isPaymentListVisible,
    isCashFormVisible,
    isCardFormVisible,
    cash,
    cards,
  } = useSelector((state) => state.checkout);

  const {
    availablePaymentsToGo,
    availablePaymentsDelivery,
    mercadoPagoLogin,
    mercadoPagoCartao,
    mercadoPagoPix,
  } = useSelector((state) => state.shop);

  const mercadoPagoEnable =
    mercadoPagoLogin || mercadoPagoCartao || mercadoPagoPix;
  const availablePaymentsOff = toGo
    ? availablePaymentsToGo
    : availablePaymentsDelivery;

  const paymentOfflineLabel = toGo ? "Pagar na loja" : "Pagar na entrega";

  useEffect(() => {
    if (!mercadoPagoEnable && !paymentOffline) {
      dispatch(clearPayment());
      dispatch(setPaymentOffline());
      return;
    }
    if (
      mercadoPagoEnable &&
      paymentOffline &&
      availablePaymentsOff.length === 0
    ) {
      dispatch(clearPayment());
      dispatch(setPaymentOnline());
      return;
    }

    if (payment) {
      if (
        !(mercadoPagoEnable && mercadoPagoPix && PIX_OPTION.name === payment) &&
        !(
          mercadoPagoEnable &&
          mercadoPagoLogin &&
          MERCADO_PAGO_OPTION.name === payment
        ) &&
        !(mercadoPagoEnable && mercadoPagoCartao && cardSelected) &&
        !availablePaymentsOff.find((p) => p.name === payment)
      ) {
        dispatch(clearPayment());
      }
    }
  }, [
    mercadoPagoEnable,
    mercadoPagoLogin,
    mercadoPagoCartao,
    mercadoPagoPix,
    paymentOffline,
    availablePaymentsOff,
    payment,
    cardSelected,
    dispatch,
  ]);

  const onToChangeClick = () => {
    dispatch(setFormAnswer({ name: "isPaymentListVisible", value: true }));
  };

  const onPaymentSelect = (selected) => {
    dispatch(setPayment(selected));
    dispatch(trackPaymentInfo(selected.name));
  };

  const cancelSelection = () => {
    dispatch(setFormAnswer({ name: "isPaymentListVisible", value: false }));
  };

  const onChangeForCashChange = (event) => {
    const value = event.target.value;
    if (value.length > 0) {
      dispatch(setFormAnswer({ name: "cash", value }));
    }
  };

  const onCashSubmit = (event) => {
    event.preventDefault();
    dispatch(setCash({ cash }));
  };

  const cancelCashForm = () => {
    dispatch(clearPayment());
  };

  const cancelCardForm = () => {
    dispatch(setFormAnswer({ name: "isCardFormVisible", value: false }));
  };

  const onCardSelection = (card) => {
    dispatch(selectCard(card));
  };

  const onRemoveCard = () => {
    dispatch(removeCard(cardToDelete));
    setCardToDelete(null);
  };

  const setNeedChangeFalse = () => {
    const value = Decimal(costs.total).times(100).toString();
    dispatch(setCash({ cash: value }));
  };

  const addNewCard = () => {
    dispatch(setFormAnswer({ name: "isPaymentListVisible", value: false }));
    dispatch(setFormAnswer({ name: "isCardFormVisible", value: true }));
  };

  const setOnline = () => {
    dispatch(setPaymentOnline());
  };

  const setOffline = () => {
    dispatch(setPaymentOffline());
  };

  const showDeleteCard = (event, card) => {
    event.stopPropagation();
    setCardToDelete(card);
  };

  const cancelDeleteCard = () => {
    setCardToDelete(null);
  };

  const isCashSelected = costs.paymentIsCash && !isCashFormVisible;
  const error = costs.paymentError !== null;

  return (
    <div>
      <CheckoutRow
        icon={
          payment ? (
            <PaymentCardImage paymentLabel={payment} />
          ) : (
            <PaymentIcon />
          )
        }
        title={
          payment
            ? isCashSelected
              ? `${payment} ${toPriceDisplay(costs.paymentValue)}`
              : payment
            : "Escolha a forma de pagamento"
        }
        actionLabel={payment ? "Trocar" : "Escolher"}
        onClick={onToChangeClick}
      >
        {isCashSelected && costs.paymentError && (
          <span className="checkout-row__error">{costs.paymentError}</span>
        )}
        {isCashSelected && !costs.paymentError && (
          <span className="checkout-row__subtitle">
            {Decimal(costs.paymentDiff).equals(0)
              ? "Sem troco"
              : `Troco ${toPriceDisplay(costs.paymentDiff)}`}
          </span>
        )}
      </CheckoutRow>

      <Modal
        show={isPaymentListVisible && !cardToDelete}
        onHide={cancelSelection}
      >
        <Modal.Header closeButton>
          <Modal.Title as="h3">Forma de pagamento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {availablePaymentsOff.length > 0 && mercadoPagoEnable && (
            <div className="pb-3 mb-3 d-flex justify-content-between">
              <Button
                className="w-100 me-3 p-2"
                variant={paymentOnline ? "shop" : "outline-shop"}
                onClick={setOnline}
              >
                <PaymentIcon className="me-2" />
                Pagar pelo app
              </Button>
              <Button
                className="w-100 ms-3 p-2"
                variant={paymentOffline ? "shop" : "outline-shop"}
                onClick={setOffline}
              >
                <DialpadIcon className="me-2" />
                {paymentOfflineLabel}
              </Button>
            </div>
          )}

          {mercadoPagoEnable && paymentOnline && (
            <>
              <ListGroup>
                {mercadoPagoLogin && (
                  <ListGroup.Item
                    action
                    type="button"
                    key="mercado_pago"
                    onClick={() => onPaymentSelect(MERCADO_PAGO_OPTION)}
                    className="payment-option"
                  >
                    <img src={mpURL} alt="" height="40" />
                    <span className="payment-option__label">
                      {MERCADO_PAGO_OPTION.name}
                    </span>
                  </ListGroup.Item>
                )}
                {mercadoPagoPix && (
                  <ListGroup.Item
                    action
                    type="button"
                    key="pix"
                    onClick={() => onPaymentSelect(PIX_OPTION)}
                    className="payment-option"
                  >
                    <img src={pixURL} alt="" height="32" />
                    <span className="payment-option__label">
                      {PIX_OPTION.name}
                    </span>
                  </ListGroup.Item>
                )}
                {mercadoPagoCartao &&
                  cards.map((card) => (
                    // Tem o botão de apagar dentro, e botão dentro de botão é
                    // HTML inválido: aqui o item é uma div com papel de botão.
                    <ListGroup.Item
                      action
                      as="div"
                      role="button"
                      tabIndex={0}
                      key={card.id}
                      onClick={() => onCardSelection(card)}
                      onKeyDown={(event) => {
                        if (
                          event.target === event.currentTarget &&
                          (event.key === "Enter" || event.key === " ")
                        ) {
                          event.preventDefault();
                          onCardSelection(card);
                        }
                      }}
                      className="payment-option"
                    >
                      <div className="d-flex flex-row align-items-center flex-grow-1">
                        <PaymentCardImage
                          paymentLabel={card.issuer_name}
                          className="me-4"
                        />
                        <div>
                          {card.issuer_name}
                          <br />
                          {`**** ${card.last_four_digits}`}
                        </div>
                      </div>
                      <Button
                        variant="link"
                        onClick={(event) => showDeleteCard(event, card)}
                      >
                        <DeleteForeverIcon />
                      </Button>
                    </ListGroup.Item>
                  ))}
              </ListGroup>
              {mercadoPagoCartao && (
                <Button
                  className="w-100 mt-3"
                  variant="shop"
                  type="submit"
                  onClick={addNewCard}
                >
                  Adicionar novo cartão
                </Button>
              )}
            </>
          )}

          {paymentOffline && (
            <ListGroup>
              {availablePaymentsOff.map((availablePayment) => (
                <ListGroup.Item
                  action
                  type="button"
                  key={availablePayment.id}
                  onClick={() => onPaymentSelect(availablePayment)}
                  className="payment-option"
                >
                  <PaymentCardImage paymentLabel={availablePayment.name} />
                  <span className="payment-option__label">
                    {availablePayment.name}
                  </span>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={isCashFormVisible} onHide={cancelCashForm}>
        <Modal.Header closeButton>
          <Modal.Title as="h3">Precisa de troco?</Modal.Title>
        </Modal.Header>
        <Form onSubmit={onCashSubmit}>
          <Modal.Body style={styles.body}>
            <Button
              variant="shop"
              className="w-100"
              onClick={setNeedChangeFalse}
            >
              Sem troco, tenho o valor exato
              <br />
              {toPriceDisplay(costs.total)}
            </Button>

            <TextInput
              style={styles.input}
              label="Troco para quanto?"
              error={error}
              helperText={costs.paymentError}
              value={cash}
              onChange={onChangeForCashChange}
              inputComponent={MoneyInput}
              required
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-shop" onClick={cancelCashForm}>
              Cancelar
            </Button>
            <Button variant="shop" type="submit" disabled={error}>
              Confirmar troco
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={isCardFormVisible} onHide={cancelCardForm}>
        <Modal.Header closeButton>
          <Modal.Title as="h3">Adicionar cartão de crédito</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CardMercadoPago onSuccess={cancelCardForm} />
        </Modal.Body>
      </Modal>

      <Modal show={!!cardToDelete} onHide={cancelDeleteCard}>
        <Modal.Header closeButton>
          <Modal.Title>Quer excluir este cartão?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cardToDelete &&
            `Ao confirmar, o cartão ${cardToDelete.issuer_name} final ${cardToDelete.last_four_digits} não estará mais disponível na sua lista de cartões salvos no aplicativo.`}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={cancelDeleteCard}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={onRemoveCard}>
            Excluir cartão
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
